<template>
  <!-- Tabs (S2_Bento right): each tab swaps a photo, words and a button.
       Real tabs (arrow keys, aria); on phones they scroll sideways. -->
  <SectionBand :heading="section.heading || section.title" :variant="variant" :background="section.background" label="Tabs">
    <div class="s2-tabs__list" role="tablist" :aria-label="section.heading || 'Tabs'">
      <button v-for="(t, i) in tabs" :key="i" :id="`${uid}-t${i}`" type="button" role="tab" class="s2-tabs__tab" :class="{on: i === active}"
              :aria-selected="i === active" :aria-controls="`${uid}-p${i}`" :tabindex="i === active ? 0 : -1"
              @click="active = i" @keydown.right.prevent="move(1)" @keydown.left.prevent="move(-1)">{{ t.label || t.title }}</button>
    </div>
    <div v-for="(t, i) in tabs" v-show="i === active" :key="i" :id="`${uid}-p${i}`" role="tabpanel" :aria-labelledby="`${uid}-t${i}`" class="s2-tabs__panel">
      <Media v-if="t.image" :media="t.image" ratio="4 / 3" class="s2-tabs__media" />
      <div>
        <h3 v-if="t.title" class="t-h2">{{ t.title }}</h3>
        <div class="s2-prose t-body" v-html="html(t.text)" />
        <a v-if="btn(t)" :href="back(btn(t).href)" class="t-btn s2-tabs__btn">{{ btn(t).label }}</a>
      </div>
    </div>
  </SectionBand>
</template>

<script setup>
import {computed, ref, useId} from "vue";
import SectionBand from "../shared/SectionBand.vue";
import Media from "../shared/Media.vue";
import {contentHtml} from "@/utils/contentHtml.js";
import {safeLink} from "@/utils/blocks.js";
import {useBackLinks} from "@/composables/useBackLinks.js";
const back = useBackLinks();

const props = defineProps({section: {type: Object, required: true}, variant: {type: [Number, String], default: 1}});
const tabs = computed(() => (Array.isArray(props.section.tabs) ? props.section.tabs : []).filter((t) => t && (t.label || t.title)).slice(0, 6));
const active = ref(0);
const uid = useId();
const html = (t) => contentHtml(t || "");
const btn = (t) => {
  const href = safeLink(t.button?.link || t.buttonLink || t.link || "");
  const label = t.button?.label || t.buttonText || t.button;
  return href && typeof label === "string" && label ? {href, label} : null;
};
function move(d) {
  const n = tabs.value.length;
  if (!n) return;
  active.value = (active.value + d + n) % n;
  document.getElementById(`${uid}-t${active.value}`)?.focus();
}
</script>

<style scoped>
.s2-tabs__list { display: flex; gap: .5rem; margin-bottom: 1.5rem; overflow-x: auto; scrollbar-width: none; }
.s2-tabs__tab { flex: none; border: 1.5px solid var(--t-line); background: var(--t-surface); color: var(--t-ink); border-radius: var(--t-btn-radius, 999px); padding: .55rem 1.1rem; font: 600 .95rem var(--t-font-body); min-height: 44px; cursor: pointer; }
.s2-tabs__tab.on { background: var(--t-primary); color: var(--t-primary-ink); border-color: var(--t-primary); }
.s2-tabs__panel { display: grid; grid-template-columns: 1.1fr 1fr; gap: clamp(1.25rem, 4vw, 3rem); align-items: center; }
.s2-tabs__btn { margin-top: 1.25rem; }
.s2-prose :deep(p) { margin: .6rem 0 0; }
@media (max-width: 760px) { .s2-tabs__panel { grid-template-columns: 1fr; } }
</style>
